<template>
  <div :id="`${source}_top_days_of_the_week_graph`">
    <ContainerInfoItem
      :source="`${source}_top_days_of_the_week_graph`"
      :title="showDailyView ? 'Top hours of the day' : 'Top Days of the Week'"
      :description="showDailyView ? `${selectedDay}` : range?.label"
      height="full"
      :back="showDailyView ? goBackToWeeklyView : null"
    >
      <SkeletonLoader v-if="isLoading" :page="loaderPages.CHART" />

      <BarChart
        v-else-if="chartCategories && chartData"
        :id="`${source}_top_days_of_the_week_chart`"
        :categories="chartCategories"
        :series-data="chartData"
        @barClick="handleBarClick"
      />

      <EmptyScreen
        v-else
        :source="`${source}_top_days_of_the_week_graph`"
        text="No data found!"
        :type="emptyScreenTypes.CHART_AND_TABLE"
      />
    </ContainerInfoItem>
  </div>
</template>

<script setup>
import { computed, ref, watchEffect, onMounted } from "vue";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import { emptyScreenTypes } from "@/src/constants/searchConstants.js";
import store from "@/src/store/index.js";
import AnalyticService from "@/src/api/AnalyticService.js";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import BarChart from "@/src/components/charts/BarChart.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import ContainerInfoItem from "@/src/components/info_components/ContainerInfoItem.vue";
import { getTimeZone, profileTimezone } from "@/src/utils/utils.js";
import { useAnalyticsRefresh } from "@/src/composables/useAnalyticsRefresh.js";

const props = defineProps({
  source: { type: String, required: true },
});

const range = computed(() => store.getters["analytics/getRange"]);
const filters = computed(() => store.getters["analytics/getFilters"]);
const userIds = computed(() => store.getters["analytics/getUserIds"]);
const teamIds = computed(() => store.getters["analytics/getTeamIds"]);

const chartCategories = ref(null);
const chartData = ref(null);
const isLoading = ref(false);
const showDailyView = ref(false);
const selectedDay = ref("");
const weeklyData = ref(null);
const timeZone = ref(getTimeZone());

const resetState = () => {
  showDailyView.value = false;
  selectedDay.value = "";
};

const getBaseParams = () => {
  return {
    action: "created",
    start_time: range.value?.start,
    end_time: range.value?.end,
    user_id: userIds.value,
    team_id: teamIds.value,
    meeting_agent_id: filters.value?.meeting_agent?.id,
    time_zone: timeZone.value,
  };
};

const processWeekDayAnalyticsData = (data) => {
  if (!data || !data.length) {
    return { categories: null, seriesData: null };
  }

  const dayOrder = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  const sortedData = [...data].sort(
    (a, b) => dayOrder[a.day_of_week] - dayOrder[b.day_of_week],
  );

  const categories = sortedData.map((item) => item.day_of_week);
  const seriesData = [
    {
      name: "Meetings Count",
      data: sortedData.map((item) => item.count),
      color: "#48AF7D",
    },
  ];

  return { categories, seriesData };
};

const processDayHourAnalyticsData = (data) => {
  if (!data || !data.length) {
    return { categories: null, seriesData: null };
  }

  const formatHour = (timeStr) => {
    const [hour] = timeStr.split(":");
    const hourNum = parseInt(hour);
    if (hourNum === 0) return "12 AM";
    if (hourNum === 12) return "12 PM";
    return hourNum < 12 ? `${hourNum} AM` : `${hourNum - 12} PM`;
  };

  const sortedData = [...data].sort((a, b) => {
    const hourA = parseInt(a.time.split(":")[0]);
    const hourB = parseInt(b.time.split(":")[0]);
    return hourA - hourB;
  });

  const categories = sortedData.map((item) => formatHour(item.time));
  const seriesData = [
    {
      name: "Meetings Count",
      data: sortedData.map((item) => item.count),
      color: "#2579EF",
    },
  ];

  return { categories, seriesData };
};

const handleBarClick = (data) => {
  const dayIndex = data.dataPointIndex;
  if (
    dayIndex >= 0 &&
    dayIndex < chartCategories.value.length &&
    !showDailyView.value
  ) {
    selectedDay.value = chartCategories.value[dayIndex];
    showDailyView.value = true;
    toFetchMeetingDayHourAnalytics(selectedDay.value);
  }
};

const goBackToWeeklyView = () => {
  showDailyView.value = false;
  selectedDay.value = "";

  if (weeklyData.value) {
    chartCategories.value = weeklyData.value.categories;
    chartData.value = weeklyData.value.seriesData;
  }
};

const toFetchMeetingWeekDayAnalytics = async () => {
  try {
    isLoading.value = true;
    const params = getBaseParams();

    const response = await AnalyticService.V2.fetchMeetingWeekDayAnalytics(params);
    if (response?.success && response?.data) {
      const data = response?.data?.data;
      const processed = processWeekDayAnalyticsData(data);

      weeklyData.value = processed;
      chartCategories.value = processed.categories;
      chartData.value = processed.seriesData;
    } else {
      chartCategories.value = null;
      chartData.value = null;
      weeklyData.value = null;
    }
  } catch (error) {
    console.error(
      "An error occurred while fetching meeting week day analytics: ",
      error,
    );
    chartCategories.value = null;
    chartData.value = null;
    weeklyData.value = null;
  } finally {
    isLoading.value = false;
  }
};

const toFetchMeetingDayHourAnalytics = async (day) => {
  try {
    isLoading.value = true;
    const params = {
      ...getBaseParams(),
      day: day,
    };

    const response = await AnalyticService.V2.fetchMeetingDayHourAnalytics(params);
    if (response?.success && response?.data) {
      const data = response?.data?.data;
      const processed = processDayHourAnalyticsData(data);

      chartCategories.value = processed.categories;
      chartData.value = processed.seriesData;
    } else {
      chartCategories.value = null;
      chartData.value = null;
    }
  } catch (error) {
    console.error(
      "An error occurred while fetching meeting day hour analytics: ",
      error,
    );
    chartCategories.value = null;
    chartData.value = null;
  } finally {
    isLoading.value = false;
  }
};

watchEffect(() => {
  if (range.value && userIds.value && teamIds.value) {
    resetState();
    toFetchMeetingWeekDayAnalytics();
  }
});

useAnalyticsRefresh(toFetchMeetingWeekDayAnalytics, [range, userIds, teamIds]);

onMounted(async () => {
  timeZone.value = await profileTimezone();
  await toFetchMeetingWeekDayAnalytics();
});
</script>
